// initialsOf — extracts up-to-2-letter initials from a personal name.
//
// Lives in packages/i18n because initial generation is locale-aware:
// Hebrew/Armenian/Russian names structure differently and we may want
// per-locale rules later (e.g. Armenian patronymics, Hebrew א'). Today
// the implementation is locale-agnostic — first letter of first word
// + first letter of last word, uppercased.
//
// Returns `null` when the input isn't a usable name (empty, phone
// number, all-numeric, etc) so callers can fall back to a generic
// avatar (icon, image, etc) instead of rendering "+" or "12".
//
// Examples:
//   initialsOf('Ruben Mkhitaryan')  → 'RM'
//   initialsOf('Pashinyan')         → 'PA'
//   initialsOf('+972527277222')     → null
//   initialsOf('  ')                → null
//   initialsOf(null)                → null

const PHONE_LIKE = /^[+\d\s\-().]+$/;

export function initialsOf(name: string | null | undefined): string | null {
  if (!name) return null;
  const trimmed = name.trim();
  if (!trimmed) return null;

  // Phone numbers and other purely-numeric tokens aren't names. Falling
  // through here would yield "+9" or "97" — visually noisy and meaningless.
  if (PHONE_LIKE.test(trimmed)) return null;

  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    // Single word: first 2 characters.
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  // Multiple words: first letter of first + first letter of last word.
  const first = parts[0]![0] ?? '';
  const last = parts[parts.length - 1]![0] ?? '';
  return (first + last).toUpperCase() || null;
}
